<script lang="ts">
	import type { PageProps } from "./$types";
	import { PortfolioMap, PortfolioAssetCards } from "#lib/web-ui/blocks/index.js";

	let { data }: PageProps = $props();

	const assets = $derived(
		[...data.assets].sort((a, b) => (b.grossLeasableArea ?? 0) - (a.grossLeasableArea ?? 0))
	);
</script>

<svelte:head>
	<title>Real Estate Investment Portfolio | Fergmar Capital</title>
	<meta
		name="description"
		content="Explore Fergmar Capital's real estate portfolio: multifamily apartments and commercial properties across South Texas."
	/>
	<meta property="og:title" content="Real Estate Investment Portfolio | Fergmar Capital" />
	<meta
		property="og:description"
		content="Explore our diversified portfolio of multifamily and commercial assets across South Texas."
	/>
	<meta name="twitter:title" content="Real Estate Investment Portfolio | Fergmar Capital" />
	<meta
		name="twitter:description"
		content="Explore our diversified portfolio of multifamily and commercial assets across South Texas."
	/>

	{@html `<script type="application/ld+json">${JSON.stringify({
		"@context": "https://schema.org",
		"@type": "ItemList",
		name: "Fergmar Capital Real Estate Portfolio",
		description:
			"Diversified portfolio of value-add and stabilized real estate assets across growing secondary markets.",
		url: "https://fergmarcapital.com/portfolio",
		numberOfItems: assets.length,
		itemListElement: assets.slice(0, 20).map((asset, i) => ({
			"@type": "ListItem",
			position: i + 1,
			item: {
				"@type": "RealEstateListing",
				name: asset.displayName,
				address: {
					"@type": "PostalAddress",
					addressLocality: asset.city ?? "Victoria",
					addressRegion: asset.state ?? "TX"
				},
				...(asset.assetType && { additionalType: asset.assetType })
			}
		}))
	})}</script>`}
</svelte:head>

<h1 class="sr-only">Fergmar Capital Real Estate Investment Portfolio</h1>

<p class="sr-only">
	Fergmar Capital manages a diversified real estate portfolio of multifamily apartments and
	commercial properties across South Texas. The portfolio includes value-add and stabilized assets
	targeting capital preservation, cash flow generation, and long-term appreciation.
</p>

<PortfolioMap {assets} />

<PortfolioAssetCards
	{assets}
	label="Our Portfolio"
	title="Assets Under Management"
	subtitle="Our portfolio reflects a long history of corporate-owned deals that have gone full cycle without outside investment, spanning multifamily communities, commercial properties, and new construction projects. Having built a proven track record of acquiring, managing, and exiting assets with our own capital, we began syndicating deals in 2022 to expand the portfolio and bring outside investors into the same disciplined, hands-on approach that has driven consistent returns across market cycles."
	theme="light"
/>
